import { getListOptions } from '@common/services';
import {
  IDatabaseColumn,
  ISelect,
  IObj,
  IMultiRadioButton,
} from '@common/types/';
import { AxiosResponse } from 'axios';
import { ceil, find, get, isEmpty, isNil, map } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

const PAGE_SIZE = 100;
const useRadioBox = (attributes: IMultiRadioButton | any) => {
  const { changeInput, valueInput, dependencies } = attributes;
  const [listOption, setListOption] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadMore, setHasLoadMore] = useState(false);

  const pageSize = get(attributes, 'field.pageSize', PAGE_SIZE);
  const totalPage = useMemo(() => ceil(total / pageSize), [total]);

  const onLoadMore = useCallback(() => {
    if (page < totalPage) {
      setPage((preState) => preState + 1);
    }
  }, [page, totalPage]);

  const getLabelField = get(attributes, 'database.options.labelField');
  const isMakeCustom = get(attributes, 'seperate.enabled', false);

  const fieldTable: any[] = [];

  const getFieldTable = find(
    fieldTable,
    (item) => item.databaseUuid === attributes.databaseUuid
  );

  const getFieldId: IDatabaseColumn | any = find(
    getFieldTable?.fields,
    (item) => item.fid === getLabelField
  );

  const optionDataOfDatabase = useMemo(() => {
    const defaultValue =
      get(attributes, 'attributes.defaultValue.text') ||
      get(attributes, 'field.binding.options.labelField');
    return map(listOption, (value: any) => {
      return {
        value: value._id || value.id,
        label: value?.[defaultValue],
      };
    }).filter(
      (item: Record<string, any>) => !isNil(item.label) || !isEmpty(item.label)
    );
  }, [listOption, getFieldTable, getFieldId]);

  const optionCustomData = useMemo(() => {
    return get(attributes, 'seperate.items', '')
      .split('\n')
      .map((item: string) => ({
        label: item.trim(),
        value: item.trim(),
      }))
      .filter(
        (item: Record<string, any>) =>
          !isNil(item.label) || !isEmpty(item.label)
      );
  }, [attributes]);

  const list = isMakeCustom ? optionCustomData : optionDataOfDatabase;

  const handleChangeValue = (value: string | number) => {
    // For radio buttons, we either select the new value or deselect if clicking the same one
    if (valueInput === value) {
      // Allow deselection if clicking the same radio button
      changeInput(attributes.allowDeselect ? null : value);
    } else {
      changeInput(value);
    }
  };

  const clearSelection = () => {
    changeInput(null);
  };

  const isSelected = (value: string | number) => {
    return valueInput === value;
  };

  useEffect(() => {
    const getListOption = async (field: IObj) => {
      const params = {
        appId: dependencies.appInfor.id,
        fieldId: field?.fieldId,
        screenUuid: attributes?.screenUuid,
        objectId: attributes?.formId,
        dependencies,
        limit: pageSize * page,
      };
      setIsLoading(true);
      getListOptions(params)
        .then((resp: AxiosResponse) => {
          setListOption(resp.data);
          setTotal(get(resp, 'total'));
          // check has load more
          if (get(resp, 'data.length') < get(resp, 'total')) {
            setHasLoadMore(true);
          } else {
            setHasLoadMore(false);
          }
        })
        .catch(() => {
          setListOption([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    if (attributes?.formId && attributes?.field && dependencies?.appInfor) {
      getListOption(attributes?.field);
    } else {
      setListOption(attributes.dataBinding || []);
    }
  }, [
    attributes?.formId,
    attributes?.field,
    dependencies?.appInfor,
    attributes.dataBinding,
    page,
  ]);

  return {
    value: valueInput,
    list,
    isLoading,
    handleChangeValue,
    onLoadMore,
    hasLoadMore,
    isSelected,
    clearSelection,
    hasSelection: !isNil(valueInput),
  };
};

export type useRadioBoxProps = ReturnType<typeof useRadioBox>;
export default useRadioBox;
